/**
 * @fileoverview Civil & Criminal Offences — tabbed finding review modal
 * @module jobs/phases/review/civilCriminalOffences/OffenceFindingModal
 *
 * Focused review of one finding with prev/next stepping. Tabs:
 *   - Overview — conduct / standard / knowledge cards, criminal overlay +
 *     defence callouts, parallel-head chips
 *   - Evidence & Sources — inherited feeder refs (linked back to their agent),
 *     the resolved source documents (openable), and provenance notes
 *   - Notes — practitioner notes (offenceFindingNotes)
 *   - Activity — the standard ChangeHistory audit trail
 *   - Locks — row / decision locks
 * Footer carries the decision actions (Pursue / Refer / Dismiss) and Ask Agent.
 */

import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { useNavigate } from "@tanstack/react-router";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { toast } from "sonner";
import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  FileText,
  Gavel,
  History,
  LayoutDashboard,
  Link2,
  CalendarRange,
  Loader2,
  Lock,
  LockOpen,
  MessageSquare,
  Pencil,
  Plus,
  ReceiptText,
  ShieldQuestion,
  StickyNote,
  Trash2,
  X,
} from "lucide-react";
import { cn } from "@/utils/misc";
import { Badge } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";
import { Textarea } from "@/ui/primitives/textarea";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/ui/composed/overlays/dialog";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/ui/composed/sections/tabs";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/ui/composed/sections/collapsible";
import { ChevronDown } from "lucide-react";
import {
  ChangeHistory,
  type ResolvedHistoryUser,
} from "@/components/jobs/phases/gather/summarization/shared-panels/ChangeHistory";
import { DocumentDetail } from "@/components/jobs/phases/gather/documents/DocumentDetail";
import { UserAvatar } from "@/ui/composed/user-avatar";
import { getDisplayName } from "@/ui/composed/user-avatar.helpers";
import {
  ChatMessageList,
  ChatInput,
  type ChatMessageData,
  type ChatUserInfo,
} from "@/ui/composed/chat";
import { Callout } from "../flaggedTransactions/Callout";
import {
  AgentText,
  AgentProse,
  StatuteBadge,
} from "../flaggedTransactions/AgentText";
import {
  fmtMoney,
  fmtCapacity,
  fmtDate,
  fmtDateTime,
  agentText,
  friendlySource,
  humaniseTokens,
  structureLegalText,
  splitStandardShortfall,
  bandForValue,
  BANDING_BADGE,
  OFFENCE_DECISION_BADGE,
  SOURCE_DATA_LABEL,
  type OffenceFindingUI,
  type OffenceHeadUI,
} from "./offenceShared";

const offences = api.jobs.summarization.agents.civilCriminalOffences;

interface TxnItem {
  flaggedId: string;
  counterparty: string;
  relationship?: string;
  date?: string;
  amount: number;
  quantum?: number;
  banding: string;
  flaggedHeadKey?: string;
  documents: Array<{ documentId: string; fileName: string }>;
}

/** The concrete inherited transactions this finding rests on — with amounts and
 *  links to the underlying documents so the practitioner can verify each one. */
function TransactionList({
  transactions,
  onOpenDoc,
}: {
  transactions: TxnItem[];
  onOpenDoc: (id: string) => void;
}) {
  const total = transactions.reduce((s, t) => s + (t.quantum ?? t.amount), 0);
  return (
    <div className="rounded-lg border">
      <div className="flex items-center justify-between border-b px-3 py-2">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Underlying transactions ({transactions.length})
        </span>
        <span className="font-mono text-sm font-semibold tabular-nums">
          {fmtMoney(total)}
        </span>
      </div>
      <div className="divide-y">
        {transactions.map((t) => {
          const doc = t.documents[0];
          return (
            <div
              key={t.flaggedId}
              className="flex items-center gap-2 px-3 py-2 text-sm"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="truncate font-medium">{t.counterparty}</span>
                  {t.flaggedHeadKey && (
                    <Badge variant="outline" size="sm" className="shrink-0">
                      {friendlySource(t.flaggedHeadKey)}
                    </Badge>
                  )}
                </div>
                <div className="text-[11px] text-muted-foreground">
                  {t.relationship ? `${t.relationship} · ` : ""}
                  {t.date ? fmtDate(t.date) : "date unconfirmed"}
                </div>
              </div>
              <span className="shrink-0 font-mono text-sm tabular-nums">
                {fmtMoney(t.quantum ?? t.amount)}
              </span>
              {doc ? (
                <button
                  type="button"
                  onClick={() => onOpenDoc(doc.documentId)}
                  title={`Open ${doc.fileName}`}
                  className="shrink-0 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  <FileText className="h-4 w-4" />
                </button>
              ) : (
                <span className="w-6 shrink-0" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** A bordered card wrapping one labelled prose section. */
function SectionCard({ label, text }: { label: string; text: string }) {
  const cleaned = humaniseTokens(text);
  const parts = splitStandardShortfall(cleaned);
  return (
    <div className="rounded-lg border bg-muted/20 p-3">
      <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
        {label}
      </p>
      {parts ? (
        <div className="space-y-3">
          <div>
            <p className="mb-1 text-xs font-semibold text-foreground">
              Standard
            </p>
            <AgentProse
              text={structureLegalText(parts.standard)}
              className="text-sm leading-relaxed"
            />
          </div>
          <div className="border-t pt-3">
            <p className="mb-1 text-xs font-semibold text-foreground">
              Shortfall
            </p>
            <AgentProse
              text={structureLegalText(parts.shortfall)}
              className="text-sm leading-relaxed"
            />
          </div>
        </div>
      ) : (
        <AgentProse
          text={structureLegalText(cleaned)}
          className="text-sm leading-relaxed"
        />
      )}
    </div>
  );
}

export function OffenceFindingModal(props: {
  jobId: Id<"jobs">;
  head: OffenceHeadUI;
  findingId: Id<"offenceFindings">;
  orderedIds: string[];
  isViewer: boolean;
  onClose: () => void;
  onNavigate: (id: string) => void;
}) {
  const { jobId, head, findingId, orderedIds, isViewer, onClose, onNavigate } =
    props;
  const navigate = useNavigate();
  const [draftNote, setDraftNote] = useState("");
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState("");
  const [chatOpen, setChatOpen] = useState(false);
  const [viewingDocId, setViewingDocId] = useState<string | null>(null);
  const [dismissOpen, setDismissOpen] = useState(false);
  const [dismissNote, setDismissNote] = useState("");

  const { data } = useQuery(
    convexQuery(offences.publicQueries.getFinding, { jobId, findingId }),
  );
  const finding = data?.finding as OffenceFindingUI | undefined;
  const notes = useMemo(
    () =>
      (data?.notes ?? []) as Array<{
        _id: Id<"offenceFindingNotes">;
        userId: Id<"users">;
        content: string;
        createdAt: number;
      }>,
    [data],
  );
  type TxnItem = {
    flaggedId: string;
    counterparty: string;
    relationship?: string;
    date?: string;
    amount: number;
    quantum?: number;
    banding: string;
    flaggedHeadKey?: string;
    documents: Array<{ documentId: string; fileName: string }>;
  };
  const sources = (data?.sources ?? {
    documents: [],
    transactions: [],
    recoverableEstimate: 0,
    inherited: [],
  }) as {
    documents: Array<{ documentId: string; fileName: string }>;
    transactions: TxnItem[];
    recoverableEstimate: number;
    inherited: Array<{
      table: string;
      flaggedHeadKey?: string;
      note?: string;
      documents: Array<{ documentId: string; fileName: string }>;
      items: TxnItem[];
      navTarget: "flagged" | "books_and_records" | null;
    }>;
  };

  const setReview = useMutation({
    mutationFn: useConvexMutation(offences.publicMutations.setFindingReview),
  });
  const addNote = useMutation({
    mutationFn: useConvexMutation(offences.publicMutations.addFindingNote),
  });
  const deleteNote = useMutation({
    mutationFn: useConvexMutation(offences.publicMutations.deleteFindingNote),
  });
  const editNote = useMutation({
    mutationFn: useConvexMutation(offences.publicMutations.editFindingNote),
  });
  const { data: currentUser } = useQuery(
    convexQuery(api.users.queries.getCurrentUser, {}),
  );
  const currentUserId = currentUser?._id;
  const goToSource = (
    navTarget: "flagged" | "books_and_records" | null,
    flaggedHeadKey?: string,
  ) => {
    onClose();
    if (navTarget === "books_and_records") {
      navigate({
        to: "/dashboard/jobs/$jobId/review/$agent",
        params: { jobId: String(jobId), agent: "books-and-records" },
      });
    } else {
      navigate({
        to: "/dashboard/jobs/$jobId/review/$agent",
        params: { jobId: String(jobId), agent: "flagged-transactions" },
        search: { head: flaggedHeadKey },
      });
    }
  };

  const idx = orderedIds.indexOf(findingId);
  const prev = idx > 0 ? orderedIds[idx - 1] : null;
  const next =
    idx >= 0 && idx < orderedIds.length - 1 ? orderedIds[idx + 1] : null;

  const historyUserIds = useMemo<Array<Id<"users">>>(() => {
    const ids = new Set<Id<"users">>();
    for (const c of finding?.changeHistory ?? []) {
      if (c.userId) ids.add(c.userId);
    }
    for (const n of notes) ids.add(n.userId);
    return Array.from(ids);
  }, [finding?.changeHistory, notes]);
  const { data: historyUsers } = useQuery({
    ...convexQuery(api.users.queries.listUsersByIds, { ids: historyUserIds }),
    enabled: historyUserIds.length > 0,
  });
  const historyUsersById = useMemo(() => {
    const map = new Map<Id<"users">, ResolvedHistoryUser>();
    for (const u of historyUsers ?? []) map.set(u._id, u);
    return map;
  }, [historyUsers]);

  if (!finding) return null;

  const banding = BANDING_BADGE[finding.banding];
  const decision = OFFENCE_DECISION_BADGE[finding.decision];

  const setDecision = (d: "pursue" | "refer" | "dismiss") => {
    setReview.mutate(
      { jobId, findingId, decision: d },
      {
        onSuccess: () =>
          toast.success(
            d === "refer"
              ? "Marked for ASIC referral"
              : `Marked ${OFFENCE_DECISION_BADGE[d].label}`,
          ),
        onError: (e) => toast.error(e instanceof Error ? e.message : "Failed"),
      },
    );
  };

  const toggleLock = (
    field: "rowLocked" | "decisionLocked",
    value: boolean,
  ) => {
    setReview.mutate({ jobId, findingId, [field]: value });
  };

  const confirmDismiss = async () => {
    const note = dismissNote.trim();
    if (note) {
      await addNote
        .mutateAsync({ jobId, findingId, content: `Dismissed — ${note}` })
        .catch(() => undefined);
    }
    setReview.mutate(
      { jobId, findingId, decision: "dismiss" },
      {
        onSuccess: () => toast.success("Finding dismissed"),
        onError: (e) => toast.error(e instanceof Error ? e.message : "Failed"),
      },
    );
    setDismissNote("");
    setDismissOpen(false);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        hideClose
        className={cn(
          "flex max-h-[85vh] gap-0 overflow-hidden p-0",
          chatOpen ? "max-w-[72rem]" : "max-w-3xl",
        )}
      >
        <div
          className={cn(
            "grid min-h-0 w-full",
            chatOpen ? "lg:grid-cols-[minmax(0,1fr)_440px]" : "grid-cols-1",
          )}
        >
          <div className="flex min-h-0 min-w-0 flex-col">
            {/* Header */}
            <div className="shrink-0 border-b px-5 pb-3 pt-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <DialogTitle className="truncate text-base font-semibold">
                    {finding.respondentName}
                  </DialogTitle>
                  <DialogDescription className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs">
                    <span>{head.name}</span>
                    <StatuteBadge>{finding.provision}</StatuteBadge>
                  </DialogDescription>
                </div>
                <div className="flex shrink-0 items-center gap-1 whitespace-nowrap">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    disabled={!prev}
                    onClick={() => prev && onNavigate(prev)}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="whitespace-nowrap text-xs tabular-nums text-muted-foreground">
                    {idx + 1} of {orderedIds.length}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    disabled={!next}
                    onClick={() => next && onNavigate(next)}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={onClose}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              {finding.conductPeriod && (
                <p className="mt-1 text-xs text-muted-foreground">
                  {agentText(finding.conductPeriod)}
                </p>
              )}
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                {finding.respondentCapacity && (
                  <Badge variant="outline" size="sm">
                    {fmtCapacity(finding.respondentCapacity)}
                  </Badge>
                )}
                <Badge color={banding.color} variant="soft" size="sm">
                  {banding.label}
                </Badge>
                <Badge color={decision.color} variant="soft" size="sm">
                  {decision.label}
                </Badge>
                {finding.criminalOverlay?.engaged && (
                  <Badge color="red" variant="soft" size="sm">
                    <AlertTriangle className="mr-1 h-3 w-3" />
                    {finding.criminalOverlay.provision} referral
                  </Badge>
                )}
                {finding.sourceDataRequired && (
                  <Badge color="amber" variant="soft" size="sm">
                    {SOURCE_DATA_LABEL}
                  </Badge>
                )}
              </div>
            </div>

            <Tabs
              defaultValue="overview"
              className="flex min-h-0 flex-1 flex-col"
            >
              <TabsList variant="underline" className="shrink-0">
                <TabsTrigger variant="underline" value="overview">
                  <LayoutDashboard className="h-3.5 w-3.5" /> Overview
                </TabsTrigger>
                <TabsTrigger variant="underline" value="transactions">
                  <ReceiptText className="h-3.5 w-3.5" /> Transactions
                  {sources.transactions.length > 0 && (
                    <span className="text-muted-foreground">
                      {sources.transactions.length}
                    </span>
                  )}
                </TabsTrigger>
                <TabsTrigger variant="underline" value="evidence">
                  <Link2 className="h-3.5 w-3.5" /> Evidence & Sources
                  {sources.documents.length > 0 && (
                    <span className="text-muted-foreground">
                      {sources.documents.length}
                    </span>
                  )}
                </TabsTrigger>
                <TabsTrigger variant="underline" value="notes">
                  <StickyNote className="h-3.5 w-3.5" /> Notes
                  {notes.length > 0 && (
                    <span className="text-muted-foreground">
                      {notes.length}
                    </span>
                  )}
                </TabsTrigger>
                <TabsTrigger variant="underline" value="activity">
                  <History className="h-3.5 w-3.5" /> Activity
                </TabsTrigger>
                <TabsTrigger variant="underline" value="locks">
                  <Lock className="h-3.5 w-3.5" /> Locks
                </TabsTrigger>
              </TabsList>

              <div className="min-h-[58vh] flex-1 overflow-y-auto px-5 py-4">
                <TabsContent value="overview" className="mt-0 space-y-4">
                  {(() => {
                    // Prefer the de-duplicated recoverable from the underlying
                    // transactions; fall back to the seeded quantum.
                    const displayQuantum =
                      sources.transactions.length > 0
                        ? sources.recoverableEstimate
                        : (finding.quantum ?? 0);
                    const hasQ = displayQuantum > 0;
                    const displayBand =
                      BANDING_BADGE[bandForValue(displayQuantum)];
                    const fallback = (() => {
                      const ss = finding.standardAndShortfall
                        ? splitStandardShortfall(
                            humaniseTokens(finding.standardAndShortfall),
                          )
                        : null;
                      if (ss?.shortfall) return ss.shortfall;
                      return humaniseTokens(finding.conductSummary);
                    })();
                    const explanation = finding.summary
                      ? humaniseTokens(finding.summary)
                      : fallback;
                    return (
                      <>
                        {/* Value banner — mirrors the flagged review modal. */}
                        <div className="rounded-lg border bg-muted/30 p-4">
                          <div className="flex items-start justify-between gap-4">
                            <div>
                              <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                                {head.hasQuantum
                                  ? "Recoverable estimate"
                                  : "Exposure"}
                              </p>
                              <p className="font-mono text-2xl font-semibold tabular-nums">
                                {hasQ ? fmtMoney(displayQuantum) : "—"}
                              </p>
                              <Badge
                                color={displayBand.color}
                                variant="soft"
                                size="sm"
                                className="mt-1"
                              >
                                {displayBand.label}
                              </Badge>
                            </div>
                            {finding.criminalOverlay?.engaged && (
                              <span className="inline-flex shrink-0 items-center gap-1 rounded-md border border-rose-200 bg-rose-50/60 px-2 py-1 text-xs text-rose-700 dark:border-rose-900/50 dark:bg-rose-950/20 dark:text-rose-400">
                                <AlertTriangle className="h-3.5 w-3.5" />
                                Criminal overlay
                                <StatuteBadge>
                                  {finding.criminalOverlay.provision}
                                </StatuteBadge>
                              </span>
                            )}
                          </div>
                          <p className="mt-3 border-t pt-3 text-sm">
                            Alleged breach of {head.name} (
                            <StatuteBadge>{finding.provision}</StatuteBadge>) by{" "}
                            <span className="font-medium">
                              {finding.respondentName}
                            </span>
                            {finding.respondentCapacity
                              ? ` (${fmtCapacity(finding.respondentCapacity)})`
                              : ""}
                            .
                          </p>
                        </div>

                        {/* Agent assessment — the plain-English bottom line. */}
                        <div>
                          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                            Agent assessment
                          </p>
                          <AgentProse
                            text={structureLegalText(explanation)}
                            className="text-sm leading-relaxed"
                          />
                        </div>

                        {finding.conductPeriod && (
                          <Callout
                            tone="info"
                            title="Review window"
                            icon={CalendarRange}
                          >
                            <AgentText
                              text={agentText(finding.conductPeriod)}
                            />
                          </Callout>
                        )}

                        {finding.sourceDataRequired &&
                          finding.sourceDataNote && (
                            <Callout
                              tone="warning"
                              title="Source data required"
                            >
                              <AgentProse
                                text={structureLegalText(
                                  humaniseTokens(finding.sourceDataNote),
                                )}
                              />
                            </Callout>
                          )}

                        {finding.defenceIndicators &&
                          finding.defenceIndicators.length > 0 && (
                            <Callout
                              tone="info"
                              title="Defence indicators"
                              icon={ShieldQuestion}
                            >
                              <ul className="list-disc space-y-0.5 pl-4">
                                {finding.defenceIndicators.map((d, i) => (
                                  <li key={i}>{humaniseTokens(d)}</li>
                                ))}
                              </ul>
                            </Callout>
                          )}

                        {finding.parallelHeadFlags &&
                          finding.parallelHeadFlags.length > 0 && (
                            <div className="flex flex-wrap items-center gap-1">
                              <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                                Also engages
                              </span>
                              {finding.parallelHeadFlags.map((h, i) => (
                                <Badge key={i} variant="outline" size="sm">
                                  {friendlySource(h)}
                                </Badge>
                              ))}
                            </div>
                          )}

                        {/* Detailed limb-by-limb analysis — collapsed by default. */}
                        <Collapsible className="rounded-lg border">
                          <CollapsibleTrigger className="group flex w-full items-center justify-between px-3 py-2.5">
                            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                              Detailed analysis
                            </span>
                            <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
                          </CollapsibleTrigger>
                          <CollapsibleContent className="space-y-3 border-t p-3">
                            <SectionCard
                              label="Conduct"
                              text={finding.conductSummary}
                            />
                            {finding.standardAndShortfall && (
                              <SectionCard
                                label="Standard & shortfall"
                                text={finding.standardAndShortfall}
                              />
                            )}
                            {finding.knowledgeBasis && (
                              <SectionCard
                                label="Knowledge basis"
                                text={finding.knowledgeBasis}
                              />
                            )}
                            {finding.criminalOverlay?.engaged && (
                              <Callout
                                tone="warning"
                                title={`Criminal overlay — ${finding.criminalOverlay.provision}`}
                                icon={Gavel}
                              >
                                <p className="mb-1">
                                  <span className="font-medium">Fault: </span>
                                  {finding.criminalOverlay.faultIndicator}
                                </p>
                                <AgentProse
                                  text={structureLegalText(
                                    humaniseTokens(
                                      finding.criminalOverlay.referralNote,
                                    ),
                                  )}
                                />
                              </Callout>
                            )}
                          </CollapsibleContent>
                        </Collapsible>
                      </>
                    );
                  })()}
                </TabsContent>

                <TabsContent value="transactions" className="mt-0">
                  {sources.transactions.length > 0 ? (
                    <TransactionList
                      transactions={sources.transactions}
                      onOpenDoc={setViewingDocId}
                    />
                  ) : (
                    <p className="py-8 text-center text-sm text-muted-foreground">
                      No underlying transactions — this head doesn't draw on the
                      recoverable-transaction feed.
                    </p>
                  )}
                </TabsContent>

                <TabsContent value="evidence" className="mt-0 space-y-4">
                  {sources.inherited.length > 0 ? (
                    <div>
                      <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                        Inherited from
                      </p>
                      <div className="space-y-2">
                        {sources.inherited.map((r, i) => (
                          <div
                            key={i}
                            className="rounded-lg border bg-muted/20 p-2.5"
                          >
                            <div className="flex items-start gap-2">
                              <Badge variant="soft" color="blue" size="sm">
                                {friendlySource(r.flaggedHeadKey ?? r.table)}
                              </Badge>
                              {r.note && (
                                <span className="flex-1 text-xs text-muted-foreground">
                                  {humaniseTokens(r.note)}
                                </span>
                              )}
                              {r.navTarget && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    goToSource(r.navTarget, r.flaggedHeadKey)
                                  }
                                  className="inline-flex shrink-0 items-center gap-1 rounded-md border bg-background px-1.5 py-0.5 text-[11px] text-muted-foreground hover:border-foreground/30 hover:text-foreground"
                                >
                                  Open
                                  <ExternalLink className="h-3 w-3" />
                                </button>
                              )}
                            </div>
                            {r.documents.length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-1.5">
                                {r.documents.map((d) => (
                                  <button
                                    key={d.documentId}
                                    type="button"
                                    onClick={() =>
                                      setViewingDocId(d.documentId)
                                    }
                                    className="inline-flex max-w-full items-center gap-1.5 rounded-md border bg-background px-2 py-1 text-xs hover:border-foreground/30 hover:bg-muted/40"
                                  >
                                    <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                                    <span className="truncate">
                                      {d.fileName}
                                    </span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      No inherited references. The agent attaches provenance as
                      it characterises the finding.
                    </p>
                  )}

                  {finding.evidence && finding.evidence.length > 0 && (
                    <div>
                      <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                        Provenance
                      </p>
                      <ul className="space-y-1.5">
                        {finding.evidence.map((e, i) => (
                          <li
                            key={i}
                            className="rounded-md border px-2.5 py-2 text-xs"
                          >
                            <AgentText text={humaniseTokens(e.note)} />
                            {e.refTable && (
                              <Badge
                                variant="outline"
                                size="sm"
                                className="ml-1.5"
                              >
                                {friendlySource(e.refTable)}
                              </Badge>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="notes" className="mt-0 flex flex-col gap-4">
                  {notes.length === 0 ? (
                    <p className="py-8 text-center text-sm text-muted-foreground">
                      No notes yet.
                    </p>
                  ) : (
                    <div className="flex flex-col gap-2">
                      {notes.map((note) => {
                        const author = historyUsersById.get(note.userId);
                        const isEditing = editingNoteId === note._id;
                        return (
                          <div
                            key={note._id}
                            className="group flex items-start gap-3 rounded-lg border bg-muted/20 px-3 py-2.5"
                          >
                            <UserAvatar
                              avatarUrl={author?.avatarUrl}
                              firstName={author?.firstName}
                              lastName={author?.lastName}
                              email={author?.email}
                              className="mt-0.5 h-7 w-7 shrink-0"
                            />
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-xs font-medium">
                                  {author
                                    ? getDisplayName(
                                        author.firstName,
                                        author.lastName,
                                        author.email,
                                      )
                                    : "Practitioner"}
                                </span>
                                <span className="text-[10px] tabular-nums text-muted-foreground">
                                  {fmtDateTime(note.createdAt)}
                                </span>
                              </div>
                              {isEditing ? (
                                <div className="mt-1.5 flex flex-col gap-2">
                                  <Textarea
                                    value={editDraft}
                                    onChange={(e) =>
                                      setEditDraft(e.target.value)
                                    }
                                    rows={2}
                                    className="resize-y p-2 text-sm leading-relaxed"
                                  />
                                  <div className="flex gap-1.5 self-end">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="h-7 text-xs"
                                      onClick={() => setEditingNoteId(null)}
                                    >
                                      Cancel
                                    </Button>
                                    <Button
                                      size="sm"
                                      className="h-7 text-xs"
                                      disabled={
                                        editNote.isPending ||
                                        editDraft.trim().length === 0
                                      }
                                      onClick={() =>
                                        editNote.mutate(
                                          {
                                            jobId,
                                            noteId: note._id,
                                            content: editDraft.trim(),
                                          },
                                          {
                                            onSuccess: () =>
                                              setEditingNoteId(null),
                                            onError: (e) =>
                                              toast.error(
                                                e instanceof Error
                                                  ? e.message
                                                  : "Failed",
                                              ),
                                          },
                                        )
                                      }
                                    >
                                      Save
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <p className="mt-1 whitespace-pre-line text-sm leading-relaxed">
                                  {note.content}
                                </p>
                              )}
                            </div>
                            {!isViewer &&
                              note.userId === currentUserId &&
                              !isEditing && (
                                <div className="flex shrink-0 items-center gap-0.5">
                                  <button
                                    type="button"
                                    className="mt-0.5 rounded p-1 text-muted-foreground/0 transition-colors hover:bg-muted hover:text-foreground group-hover:text-muted-foreground"
                                    title="Edit note"
                                    onClick={() => {
                                      setEditingNoteId(note._id);
                                      setEditDraft(note.content);
                                    }}
                                  >
                                    <Pencil className="h-3.5 w-3.5" />
                                  </button>
                                  <button
                                    type="button"
                                    className="mt-0.5 rounded p-1 text-muted-foreground/0 transition-colors hover:bg-muted hover:text-red-600 group-hover:text-muted-foreground"
                                    title="Delete note"
                                    onClick={() =>
                                      deleteNote.mutate(
                                        {
                                          jobId,
                                          noteId: note._id,
                                        },
                                        {
                                          onError: (e) =>
                                            toast.error(
                                              e instanceof Error
                                                ? e.message
                                                : "Failed",
                                            ),
                                        },
                                      )
                                    }
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                              )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {!isViewer && (
                    <div className="flex flex-col gap-2 border-t pt-3">
                      <Textarea
                        value={draftNote}
                        onChange={(e) => setDraftNote(e.target.value)}
                        rows={2}
                        placeholder="Add a note for the file…"
                        className="resize-y p-3 text-sm leading-relaxed"
                        onKeyDown={(e) => {
                          if (
                            e.key === "Enter" &&
                            (e.metaKey || e.ctrlKey) &&
                            draftNote.trim()
                          ) {
                            addNote.mutate(
                              { jobId, findingId, content: draftNote.trim() },
                              { onSuccess: () => setDraftNote("") },
                            );
                          }
                        }}
                      />
                      <Button
                        size="sm"
                        className="h-8 gap-1.5 self-end"
                        disabled={
                          addNote.isPending || draftNote.trim().length === 0
                        }
                        onClick={() =>
                          addNote.mutate(
                            { jobId, findingId, content: draftNote.trim() },
                            { onSuccess: () => setDraftNote("") },
                          )
                        }
                      >
                        {addNote.isPending ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Plus className="h-3.5 w-3.5" />
                        )}
                        Add Note
                      </Button>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="activity" className="mt-0">
                  {finding.changeHistory.length > 0 ? (
                    <ChangeHistory
                      history={finding.changeHistory}
                      usersById={historyUsersById}
                      title="Activity"
                    />
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      No activity yet.
                    </p>
                  )}
                </TabsContent>

                <TabsContent value="locks" className="mt-0 space-y-3">
                  <LockRow
                    label="Row lock"
                    description="Freeze the whole finding — agent re-runs park their updates as proposals instead of overwriting."
                    locked={finding.rowLocked}
                    disabled={isViewer}
                    onToggle={(v) => toggleLock("rowLocked", v)}
                  />
                  <LockRow
                    label="Decision lock"
                    description="Freeze only the decision field, so an agent re-run can still refresh the characterisation."
                    locked={finding.decisionLocked}
                    disabled={isViewer}
                    onToggle={(v) => toggleLock("decisionLocked", v)}
                  />
                </TabsContent>
              </div>
            </Tabs>

            {/* Footer — decision actions + ask agent */}
            {!isViewer && (
              <div className="flex shrink-0 items-center gap-2 border-t px-5 py-3">
                <Button
                  size="sm"
                  className="gap-1.5"
                  disabled={finding.decisionLocked}
                  onClick={() => setDecision("pursue")}
                >
                  <Gavel className="h-3.5 w-3.5" /> Pursue
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1.5 border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100 hover:text-rose-800 dark:border-rose-900/60 dark:bg-rose-950/30 dark:text-rose-300"
                  disabled={finding.decisionLocked}
                  onClick={() => setDecision("refer")}
                >
                  <AlertTriangle className="h-3.5 w-3.5" /> Mark for ASIC
                  referral
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1.5"
                  disabled={finding.decisionLocked}
                  onClick={() => setDismissOpen(true)}
                >
                  <X className="h-3.5 w-3.5" /> Dismiss
                </Button>
                <Button
                  size="sm"
                  variant={chatOpen ? "secondary" : "ghost"}
                  className="ml-auto gap-1.5"
                  onClick={() => setChatOpen((v) => !v)}
                >
                  <MessageSquare className="h-3.5 w-3.5" />
                  {chatOpen ? "Hide chat" : "Ask Agent"}
                </Button>
              </div>
            )}
          </div>
          {chatOpen && (
            <FindingChatColumn
              jobId={jobId}
              head={head}
              finding={finding}
              onClose={() => setChatOpen(false)}
            />
          )}
        </div>
      </DialogContent>

      <DocumentDetail
        documentId={viewingDocId as Id<"documents"> | null}
        isViewer
        open={viewingDocId !== null}
        onClose={() => setViewingDocId(null)}
      />

      <Dialog
        open={dismissOpen}
        onOpenChange={(o) => {
          if (!o) {
            setDismissOpen(false);
            setDismissNote("");
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogTitle className="text-base font-semibold">
            Dismiss this finding
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            Add a note (optional) — it's saved to the file as the reason for
            dismissal and recorded in the activity history.
          </DialogDescription>
          <Textarea
            value={dismissNote}
            onChange={(e) => setDismissNote(e.target.value)}
            rows={3}
            placeholder="Reason for dismissal…"
            className="resize-y p-3 text-sm leading-relaxed"
          />
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setDismissOpen(false);
                setDismissNote("");
              }}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="gap-1.5"
              disabled={addNote.isPending}
              onClick={() => void confirmDismiss()}
            >
              <X className="h-3.5 w-3.5" /> Dismiss
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

/** Chat column beside the modal — talks to the offences master agent, primed
 *  with this finding's context. Uses the canonical shared chat components,
 *  same as the flagged review modal. */
function FindingChatColumn(props: {
  jobId: Id<"jobs">;
  head: OffenceHeadUI;
  finding: OffenceFindingUI;
  onClose: () => void;
}) {
  const { jobId, head, finding, onClose } = props;
  const [pending, setPending] = useState<string | null>(null);

  const { data: chatMessages } = useQuery(
    convexQuery(offences.publicQueries.listMasterChat, { jobId }),
  );
  const { data: currentUser } = useQuery(
    convexQuery(api.users.queries.getCurrentUser, {}),
  );
  const userInfo = useMemo<ChatUserInfo | undefined>(() => {
    if (!currentUser) return undefined;
    return {
      avatarUrl: currentUser.avatarUrl,
      firstName: currentUser.firstName,
      lastName: currentUser.lastName,
      email: currentUser.email,
    };
  }, [currentUser]);

  const { mutateAsync: send, isPending } = useMutation({
    mutationFn: useConvexMutation(
      offences.publicMutations.sendMasterChatMessage,
    ),
  });

  const realMessages: ChatMessageData[] = useMemo(
    () =>
      (
        (chatMessages ?? []) as Array<{
          _id: string;
          role: string;
          body: string;
          createdAt: number;
        }>
      )
        .filter((m) => m.role === "user" || m.role === "assistant")
        .map((m) => ({
          id: m._id,
          role: m.role as "user" | "assistant",
          content: m.body,
          createdAt: m.createdAt,
        })),
    [chatMessages],
  );

  const messages: ChatMessageData[] = useMemo(() => {
    if (!pending) return realMessages;
    return [
      ...realMessages,
      {
        id: "optimistic-pending",
        role: "user" as const,
        content: pending,
        createdAt: 0,
      },
    ];
  }, [realMessages, pending]);

  useEffect(() => {
    if (!pending) return;
    if (
      realMessages.some((m) => m.role === "user" && m.content.endsWith(pending))
    ) {
      setPending(null);
    }
  }, [realMessages, pending]);

  const lastMessage = messages[messages.length - 1];
  const isThinking = isPending || lastMessage?.role === "user";

  const handleSend = async (raw: string) => {
    const text = raw.trim();
    if (!text) return;
    const body = `Re: ${finding.respondentName} — ${head.name} (${finding.provision}). ${text}`;
    setPending(text);
    try {
      await send({ jobId, body });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to send");
      setPending(null);
    }
  };

  return (
    <div className="hidden h-full min-h-0 w-full min-w-0 flex-col border-l bg-muted/10 lg:flex">
      <div className="flex items-center gap-2 border-b py-3 pl-4 pr-3">
        <MessageSquare className="h-4 w-4 shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium leading-none">
            Agent Chat
          </p>
          <p className="mt-0.5 truncate text-[11px] text-muted-foreground">
            About {finding.respondentName} · {head.name}
          </p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 shrink-0"
          onClick={onClose}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
      <ChatMessageList
        messages={messages}
        userInfo={userInfo}
        isThinking={isThinking}
        emptyHeadline="Ask about this finding"
        emptyMessage={`This chat is primed with ${finding.respondentName}'s ${head.name} finding and can answer questions or route work to the agent (e.g. "check the cessation date for this director").`}
        className="min-h-0 flex-1"
      />
      <div className="border-t p-3">
        <ChatInput
          onSend={(m) => void handleSend(m)}
          isDisabled={isPending}
          placeholder={`Ask about ${finding.respondentName}…`}
        />
      </div>
    </div>
  );
}

function LockRow(props: {
  label: string;
  description: string;
  locked: boolean;
  disabled: boolean;
  onToggle: (v: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-3 rounded-md border px-3 py-2.5">
      <div>
        <p className="text-sm font-medium">{props.label}</p>
        <p className="text-xs text-muted-foreground">{props.description}</p>
      </div>
      <Button
        size="sm"
        variant={props.locked ? "default" : "outline"}
        className={cn(
          "shrink-0 gap-1.5",
          props.locked && "bg-violet-600 hover:bg-violet-700",
        )}
        disabled={props.disabled}
        onClick={() => props.onToggle(!props.locked)}
      >
        {props.locked ? (
          <Lock className="h-3.5 w-3.5" />
        ) : (
          <LockOpen className="h-3.5 w-3.5" />
        )}
        {props.locked ? "Locked" : "Unlocked"}
      </Button>
    </div>
  );
}
